import { useEffect, type MutableRefObject } from "react";
import { doc, onSnapshot, type Firestore } from "firebase/firestore";
import type { EditFormState } from "../components/EditModal";

type Params = {
  db: Firestore;
  editId: string | null;
  isEditOpen: boolean;
  currentUserString: () => string | null;
  isCustomerPortal: boolean;
  setEditForm: (updater: (prev: EditFormState) => EditFormState) => void;
  setEditIsReadOnly: (v: boolean) => void;
  openedReadOnlyDueToDeleteRef: MutableRefObject<boolean>;
  setDeleteClearedWhileOpen: (v: boolean) => void;
};

export function useEditLiveSync({
  db,
  editId,
  isEditOpen,
  currentUserString,
  isCustomerPortal,
  setEditForm,
  setEditIsReadOnly,
  openedReadOnlyDueToDeleteRef,
  setDeleteClearedWhileOpen,
}: Params) {
  useEffect(() => {
    if (!editId || !isEditOpen) return;
    const ref = doc(db, "itInventory", editId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const cur = snap.data() as any;
      setEditForm((prev) => ({
        ...prev,
        deletePending: !!cur.deletePending,
        deleteMarkedBy: cur.deleteMarkedBy ?? null,
        deleteMarkedAt: cur.deleteMarkedAt ?? null,
      }));
      const me = currentUserString();
      const deleteOwnedByOther = !!cur.deletePending && !!cur.deleteMarkedBy && cur.deleteMarkedBy !== me;
      if (openedReadOnlyDueToDeleteRef.current && !deleteOwnedByOther) {
        setDeleteClearedWhileOpen(true);
      }
      const shouldBeReadOnly =
        isCustomerPortal || !!cur.invoiceReportId || deleteOwnedByOther || openedReadOnlyDueToDeleteRef.current;
      setEditIsReadOnly(shouldBeReadOnly);
    });
    return () => unsub();
  }, [
    db,
    editId,
    isEditOpen,
    currentUserString,
    isCustomerPortal,
    setEditForm,
    setEditIsReadOnly,
    openedReadOnlyDueToDeleteRef,
    setDeleteClearedWhileOpen,
  ]);
}
